import { Link } from '@tanstack/react-router';
import { Button, Card } from '@wundo/ui';
import type { OrganisationSuggestion } from '@/lib/organisations.ts';

export interface OrganisationSuggestionNotesProps {
  suggestion: OrganisationSuggestion;
  onStartOver: () => void;
}

const TENANT_NOTES: Record<OrganisationSuggestion['microsoft'], string> = {
  found: 'Microsoft Teams tenant found and filled in.',
  not_microsoft:
    'Not a Microsoft organisation: no Teams tenant found. Fill it in yourself if you have it.',
  failed:
    'Microsoft did not answer, so the Teams tenant is empty. Look up the domain again later, or fill it in yourself if you have it.',
};

function domainsNote({ domain, internal_domains }: OrganisationSuggestion): string {
  if (internal_domains.length > 1) {
    return `${internal_domains.length} internal domains found at Microsoft.`;
  }
  return `Internal domains: only ${domain}. Add any other domain the company's email addresses use.`;
}

/** What the domain lookup found and what it did not, above the form it filled in. */
export function OrganisationSuggestionNotes({
  suggestion,
  onStartOver,
}: OrganisationSuggestionNotesProps) {
  const existing = suggestion.existing_organisation;

  return (
    <Card className='flex flex-col gap-3'>
      <h2 className='text-md font-semibold text-ink'>
        What we found for {suggestion.domain}
      </h2>

      {existing ? (
        <p
          role='alert'
          className='rounded-md bg-danger-fill px-4 py-3 text-sm font-semibold text-danger'
        >
          This Microsoft organisation is already in Wundo as{' '}
          <Link
            to='/organisations/$id'
            params={{ id: existing.id }}
            className='underline underline-offset-2'
          >
            {existing.name}
          </Link>
          . Open it instead of creating it a second time.
        </p>
      ) : null}

      <ul className='flex list-disc flex-col gap-1.5 pl-5 text-sm text-ink-secondary'>
        <li>
          Name guessed from the domain. Check it and write it the way the customer does.
        </li>
        <li>{TENANT_NOTES[suggestion.microsoft]}</li>
        <li>{domainsNote(suggestion)}</li>
      </ul>

      <div>
        <Button variant='link' onClick={onStartOver}>
          Look up another domain
        </Button>
      </div>
    </Card>
  );
}
